import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Gift } from "lucide-react";
import { opApi, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// First-time setup for a "Loyalty only" plan: create the reward program, then you're done.
export function LoyaltySetup({ open, onDone }) {
  const [f, setF] = useState({ title: "Rewards", earn_type: "stamps", goal: 10, reward: "Free item" });
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState(null);
  const [agree, setAgree] = useState({ tos: false, dpa: false });
  const [showDocs, setShowDocs] = useState(false);
  useEffect(() => { if (open && !docs) opApi.get("/tenant/legal-docs").then(({ data }) => setDocs(data)).catch(() => {}); }, [open, docs]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      await opApi.post("/tenant/legal/accept", { version: docs?.version, tos: true, dpa: true });
      const goal = Math.max(1, Number(f.goal) || 10);
      const { data: existing } = await opApi.get("/tenant/loyalty/programs");
      if (!(existing || []).length) {
        await opApi.post("/tenant/loyalty/programs", { title: f.title, earn_type: f.earn_type, earn_rate: 1, goal, reward: f.reward,
          tiers: [{ goal, reward: f.reward, reward_type: "custom", reward_value: "" }], enrollment: "auto", renewable: true, active: true });
      }
      await opApi.put("/tenant/settings", { business_type: "loyalty", modes: [], onboarded: true });
      toast.success("Your loyalty program is live");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open}>
      <DialogContent className="rounded-none [&>button]:hidden" data-testid="loyalty-setup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5" /> Set up your loyalty program</DialogTitle>
          <DialogDescription>Customers earn a stamp or points each visit. Staff add them by phone number at the counter.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs text-muted-foreground">Program name<Input className="rounded-none mt-0.5" value={f.title} onChange={set("title")} data-testid="loyalty-setup-title" /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-muted-foreground">Customers earn
              <select className="mt-0.5 w-full border border-input bg-background h-9 px-2 text-sm" value={f.earn_type} onChange={set("earn_type")} data-testid="loyalty-setup-earn">
                <option value="stamps">Stamps (one per visit)</option><option value="points">Points</option>
              </select>
            </label>
            <label className="text-xs text-muted-foreground">Reward after<Input className="rounded-none mt-0.5" type="number" min="1" value={f.goal} onChange={set("goal")} data-testid="loyalty-setup-goal" /></label>
          </div>
          <label className="block text-xs text-muted-foreground">Reward<Input className="rounded-none mt-0.5" value={f.reward} onChange={set("reward")} data-testid="loyalty-setup-reward" /></label>
          <div className="border-t border-border pt-3 space-y-2 text-sm">
            <button type="button" className="text-xs underline" onClick={() => setShowDocs((v) => !v)} data-testid="loyalty-setup-read-terms">{showDocs ? "Hide" : "Read"} the terms</button>
            {showDocs && (
              <div className="max-h-48 overflow-y-auto border border-border p-3 text-xs text-muted-foreground" data-testid="loyalty-setup-terms"
                dangerouslySetInnerHTML={{ __html: `${docs?.tos_html || "Loading…"}${docs?.dpa_html || ""}` }} />
            )}
            <label className="flex items-start gap-2"><input type="checkbox" className="mt-1" checked={agree.tos} onChange={(e) => setAgree((a) => ({ ...a, tos: e.target.checked }))} data-testid="loyalty-setup-accept-tos" />
              <span>I agree to the <strong>MorrCart Platform Terms of Service</strong>.</span></label>
            <label className="flex items-start gap-2"><input type="checkbox" className="mt-1" checked={agree.dpa} onChange={(e) => setAgree((a) => ({ ...a, dpa: e.target.checked }))} data-testid="loyalty-setup-accept-dpa" />
              <span>I agree to the <strong>Data Processing Addendum</strong> and can accept for my business.</span></label>
          </div>
        </div>
        <DialogFooter>
          <Button className="rounded-none" disabled={busy || !f.title.trim() || !f.reward.trim() || !agree.tos || !agree.dpa || !docs} onClick={save} data-testid="loyalty-setup-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Start my program
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
